import { Property } from "csstype";

import { Length } from "../Utils/CssLength";
import { ThemeObject } from "../Utils/ThemeObject";

import { ThemeColor } from "./ThemeColor";

export interface ICandyThemeTypography {
    link: {
        color: ThemeColor;
        inactiveColor: ThemeColor;
    };
    fontFamily: {
        regular: string;
        monospace: string;
    };
    getEstimatedTextWidthInPxForPrimaryFont: (text: string) => number;
    primary: ThemeObject<{
        fontSize: Length;
        lineHeight: Length;
        color: ThemeColor;
    }>;
    secondary: ThemeObject<{
        color: ThemeColor;
    }>;
    inactive: ThemeObject<{
        color: ThemeColor;
    }>;
    smallFont: ThemeObject<{
        color: ThemeColor;
        fontSize: Length;
        lineHeight: Length;
        fontWeight: Property.FontWeight;
    }>;
    header2: ThemeObject<{
        fontSize: Length;
        lineHeight: Length;
        fontWeight: Property.FontWeight;
    }>;
    header3: ThemeObject<{
        fontSize: Length;
        lineHeight: Length;
        fontWeight: Property.FontWeight;
    }>;
    header4: ThemeObject<{
        fontSize: Length;
        lineHeight: Length;
        fontWeight: Property.FontWeight;
    }>;
    subheader: {
        color: {
            primary: ThemeColor;
            secondary: ThemeColor;
            link: ThemeColor;
        };
    };
}

export interface ICandyTheme {
    rootColorScheme: string | undefined;
    size: ((value: number) => number) & ((value: number | string | Array<string | number>) => string | number);
    loadingOverlayBackgroundColor: ThemeColor;
    typography: ICandyThemeTypography;
    pageLayout: {
        sidebar: {
            width: Length;
            wideModeWidth: Length;
        };
    };
    keyPalette: {
        primaryBackgroundColor: ThemeColor;
        panelBackgroundColor: ThemeColor;
        panel2BackgroundColor: ThemeColor;
        panel2BackgroundColorAsBlackTransparent: ThemeColor;
        panel3BackgroundColor: ThemeColor;
    };
    brandColors: {
        primaryColor: ThemeColor;
        panelColor: ThemeColor;
    };
    modals: {
        headerFont: ThemeObject<{
            fontSize: Length;
            lineHeight: Length;
            fontWeight: Property.FontWeight;
        }>;
    };
    dropdown: {
        arrowIconColor: ThemeColor;
    };
    menu: {
        hoveredBackgroundColor: ThemeColor;
    };
    invertedColors: {
        backgroundColor: ThemeColor;
        hoveredColor: ThemeColor;
        color: ThemeColor;
        inactiveColor: ThemeColor;
    };
    icons: {
        color: ThemeColor;
        disabledColor: ThemeColor;
        hoverColor: ThemeColor;
        hoverBackgroundColor: ThemeColor;
    };
    semanticColors: {
        action: {
            backgroundColor: ThemeColor;
            alternativeColor: ThemeColor;
            iconColor: ThemeColor;
            color: ThemeColor;
        };
        danger: {
            backgroundColor: ThemeColor;
            alternativeColor: ThemeColor;
            iconColor: ThemeColor;
            color: ThemeColor;
        };
        warning: {
            backgroundColor: ThemeColor;
            alternativeColor: ThemeColor;
            iconColor: ThemeColor;
            color: ThemeColor;
        };
        success: {
            backgroundColor: ThemeColor;
            alternativeColor: ThemeColor;
            iconColor: ThemeColor;
            color: ThemeColor;
        };
    };
    document: {
        bodyBackgroundColor: ThemeColor;
    };
    lists: {
        dividerColor: ThemeColor;
        items: {
            default: {
                backgroundColor: undefined | ThemeColor;
                secondaryColor: ThemeColor;
            };
            accentLow: {
                backgroundColor: ThemeColor;
            };
            accentHigh: {
                backgroundColor: ThemeColor;
            };
        };
    };
    border: {
        color: ThemeColor;
        solid: string;
        dashed: string;
    };
    valueControls: {
        highlightedBorderColor: ThemeColor;
        disabledCursorTextShadowColor: ThemeColor;
        blinkColor: ThemeColor;
        focusedInsetColor: ThemeColor;
        placeholderColor: ThemeColor;
    };
    tables: {
        headers: {
            smallFont: ThemeObject<{
                color: ThemeColor;
                fontSize: Length;
                lineHeight: Length;
                fontWeight: Property.FontWeight;
            }>;
        };
        border: {
            color: ThemeColor;
            solid: string;
        };
    };
    legacy: {
        scrollBarBackgroundColor: ThemeColor;
        shadowColor: ThemeColor;
    };
    graphics: {
        light: {
            strokeColor: ThemeColor;
            fillColor: ThemeColor;
        };
        dark: {
            strokeColor: ThemeColor;
            fillColor: ThemeColor;
        };
    };
    reactUISizes: {
        input: {
            height: Length;
        };
        button: {
            paddingLeft: Length;
            paddingRight: Length;
        };
        modal: {
            rightPadding: Length;
            leftPadding: Length;
            bottomPadding: Length;
        };
    };
    patchFufCombineMethods: {
        border: ThemeColor;
        hoveredBackgroundColor: ThemeColor;
    };
    banner: {
        light: {
            color: ThemeColor;
            backgroundColor: ThemeColor;
            closeCrossIconColor: ThemeColor;
        };
        dark: {
            color: ThemeColor;
            backgroundColor: ThemeColor;
            closeCrossIconColor: ThemeColor;
        };
    };
    cardActualInfo: {
        backgroundLightGreenColor: ThemeColor;
        backgroundDarkGreenColor: ThemeColor;
        linkColor: ThemeColor;
        backgroundRedColor: ThemeColor;
    };
}

export function sizeToken(tokens: number): number {
    return tokens * 8;
}
